import React, { useEffect, useMemo, useState } from 'react'
import { AlertMessages, FormDialog, FormSelect, FormTextField, Loading } from '../../../components'
import { Box, Tooltip, Typography } from '@material-ui/core';
import { getI18n } from '../../../utils/intl';
import { useIntl } from 'react-intl';
import HelpIcon from '@material-ui/icons/Help';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmailSettingsCertificates, generateCsrCertificate } from '../../../actions/emailSettings';
import { getCreatedCsrCertificate, getCreatedCsrCertificateError, getIsLoading } from '../../../reducers/emailSettings';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PropTypes from 'prop-types';

function GenerateCsrCertificate(props) {
  const { showCsr, setShowCsr, classes } = props;

  const certificatePayload = {
    commonName: "",
    organization: "",
    department: "",
    city: "",
    state: "",
    country: "",
    aliasName: "",
    keySize: null,
    uuid: "{{GENERATED_GUID}}",
  }

  const [certificateDataDetails, setCertificateDataDetails] = useState({ ...certificatePayload })
  const [showCertificate, setShowCertificate] = useState(false);
  const [showCertificateErrorAlert, setShowCertificateErrorAlert] = useState(false);

  const intl = getI18n(useIntl());

  const csrCertificate = useSelector((state) => getCreatedCsrCertificate(state));
  const csrCertificateError = useSelector((state) => getCreatedCsrCertificateError(state))
  const isLoading = useSelector((state) => getIsLoading(state))

  const commonNameClass = clsx(classes.helpIcon, classes.commonName);
  const aliasNameClass = clsx(classes.helpIcon, classes.aliasName);

  const keySizeData = [
    {
      uuid: "2048",
      name: "2048",
    },
    {
      uuid: "3072",
      name: "3072",
    },
    {
      uuid: "4096",
      name: "4096",
    },
  ]

  const handleChange = (value, fieldName) => {
    setCertificateDataDetails({ ...certificateDataDetails, [fieldName]: value });
  }

  const isDisableProceedBtn = useMemo(() => {
    if (certificateDataDetails.commonName === "" ||
      certificateDataDetails.aliasName === "" ||
      !certificateDataDetails.keySize) {
      return true;
    }
    return false;
  }, [certificateDataDetails])

  useEffect(() => {
    if (csrCertificateError) {
      setShowCertificateErrorAlert(true);
    } else {
      setShowCertificateErrorAlert(false);
    }
  }, [showCsr, csrCertificateError])

  useEffect(() => {
    if(csrCertificate) {
      setShowCsr(false);
      setShowCertificate(true);
    }
  }, [csrCertificate])

  const dispatch = useDispatch();

  const handleGenerateCertificate = () => {
    dispatch(generateCsrCertificate(certificateDataDetails))
  }

  const handleDownloadCertificate = () => {
    const pemContent = csrCertificate?.csrContent;
    const blob = new Blob([pemContent], { type: 'text/plain' });

    const link = document.createElement('a');
    link.download = `${csrCertificate?.aliasName}.pem`;
    link.href = window.URL.createObjectURL(blob);
    link.click();
    setShowCertificate(false);
    setCertificateDataDetails(certificatePayload);
    dispatch(fetchEmailSettingsCertificates());
  }

  const handleCopyToClipboard = () => {
    const content = csrCertificate?.csrContent;

    navigator.clipboard.writeText(content)
      .then(() => {
        setShowCertificate(false);
        setCertificateDataDetails(certificatePayload);
        dispatch(fetchEmailSettingsCertificates());
      })
  }

  const successMessageClass = clsx(classes.certSuccessMessage, classes.flex)

  const commonNameToolTip = <Box>
    {intl.getI18nMessage("label.email.settings.csr.certificate.common.name.tooltip")}
    <Typography className={classes.commonNameToolTip}>
      <span className={classes.commonNameRecommend}>{intl.getI18nMessage("label.email.settings.csr.certificate.common.name.recommend.tooltip")}</span>
      <span>{intl.getI18nMessage("label.email.settings.csr.certificate.common.name.recommend.tooltip.helptext")}</span>
    </Typography>
  </Box>

  const aliasNameToolTip = intl.getI18nMessage("label.email.settings.csr.certificate.alias.name.tooltip")

  return (
    <div>
      {
        isLoading &&
        <Loading />
      }
      <FormDialog
        isOpen={showCsr}
        title={intl.getI18nMessage("label.email.settings.csr.certificate.title")}
        cancelText={intl.getI18nMessage("label.dialog.unsaved.changes.cancel")}
        submitText={intl.getI18nMessage("label.email.settings.csr.certificate.button.proceed")}
        onCancel={() => setShowCsr(false)}
        submitButtonDisabled={isDisableProceedBtn}
        onSubmit={handleGenerateCertificate}
      >
        <Typography data-apim-test='generate-csr-description' >{intl.getI18nMessage("label.email.settings.csr.certificate.helptext")}</Typography>
        <Box className={classes.toolTipContainer}>
          <Tooltip title={commonNameToolTip} arrow placement="right">
            <HelpIcon className={commonNameClass} />
          </Tooltip>
          <FormTextField
            id="generate-csr-common-name"
            name={intl.getI18nMessage("label.email.settings.csr.certificate.common.name.title")}
            handleChange={(value) => handleChange(value, "commonName")}
            value={certificateDataDetails.commonName}
          />
        </Box>

        <Box className={classes.toolTipContainer}>
          <Tooltip title={aliasNameToolTip} arrow placement="right">
            <HelpIcon className={aliasNameClass} />
          </Tooltip>
          <FormTextField
            id="generate-csr-alias-name"
            name={intl.getI18nMessage("label.email.settings.csr.certificate.alias.name.title")}
            handleChange={(value) => handleChange(value, "aliasName")}
            value={certificateDataDetails.aliasName}
          />
        </Box>

        <FormSelect
          id="generate-csr-rsa-key-size"
          name={intl.getI18nMessage("label.email.settings.csr.certificate.key.size.name.title")}
          defaultText={intl.getI18nMessage("label.email.settings.csr.certificate.key.size.option")}
          data={keySizeData}
          handleChange={(e) => handleChange(e.target.value, 'keySize')}
          value={certificateDataDetails.keySize}
        />
        <FormTextField
          id="generate-csr-organization"
          name={intl.getI18nMessage("label.organization")}
          handleChange={(value) => handleChange(value, "organization")}
          value={certificateDataDetails.organization}
          optional
        />
        <FormTextField
          id="generate-csr-department"
          name={intl.getI18nMessage("label.email.settings.csr.certificate.department.title")}
          handleChange={(value) => handleChange(value, "department")}
          value={certificateDataDetails.department}
          optional
        />
        <FormTextField
          id="generate-csr-city"
          name={intl.getI18nMessage("label.email.settings.csr.certificate.city.title")}
          handleChange={(value) => handleChange(value, "city")}
          value={certificateDataDetails.city}
          optional
        />
        <FormTextField
          id="generate-csr-state"
          name={intl.getI18nMessage("label.state")}
          handleChange={(value) => handleChange(value, "state")}
          value={certificateDataDetails.state}
          optional
        />
        <FormTextField
          id="generate-csr-country"
          name={intl.getI18nMessage("label.email.settings.csr.certificate.country.title")}
          handleChange={(value) => handleChange(value, "country")}
          value={certificateDataDetails.country}
          optional
        />
        {
          showCertificateErrorAlert &&
          <AlertMessages
            id="generate-csr-alert-error"
            variant={"error"}
            message={csrCertificateError}
            className={classes.errorDialog}
            onClose={() => {
              setShowCertificateErrorAlert(false);
            }}
          />
        }
      </FormDialog>

      <FormDialog
        isOpen={showCertificate}
        title={intl.getI18nMessage("label.email.settings.csr.certificate.title")}
        hideCancelButton
        submitText={intl.getI18nMessage("label.email.settings.csr.certificate.button.save.and.finish")}
        onSubmit={handleDownloadCertificate}
        additionalBtnText={intl.getI18nMessage("label.email.settings.csr.certificate.copy.to.clipboard.button")}
        onAdditionalBtnClick={handleCopyToClipboard}
      >
        <Box className={successMessageClass}>
          <CheckCircleIcon
            className={classes.successIcon}
          />
          <Typography data-apim-test='generate-csr-success-message' className={classes.successMessageText}>{intl.getI18nMessage("label.email.settings.csr.certificate.success.message")}</Typography>
        </Box>
        {csrCertificate?.csrContent}
      </FormDialog>
    </div>
  )
}

GenerateCsrCertificate.propTypes = {
  showCsr: PropTypes.bool,
  setShowCsr: PropTypes.func,
  classes: PropTypes.object,
}

export default GenerateCsrCertificate;
